/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useReducer, createContext, useContext } from "react";
import { Switch, useParams, Route, withRouter, Link } from "react-router-dom";
import fetchHelper from "../../services/fetchHelper";
import Information from "./SiteInformation";
import FacilityDoc from "./FacilityDoc";
import PatientEngagement from "./PatientEngagement";
import FacilityEsignReport from "./FacilityEsignReport";
import RemoteMonitoring from "./RemoteMonitoring";
import savingspinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import { LayoutContext } from "../../components/layout";

export const LocationsContext = createContext({});

const SiteInformationActions = () => {
  const { information, setInformation } = useContext(LocationsContext);
  return (
    <div id="site-information-btn-container" className="axnBtn" style={{ display: "inline-flex" }}>
      {information.openForm ? (
        <>
          <img id="loading-spinner-1" src={savingspinner} style={{ width: 0, height: 0, opacity: 0 }} />
          <button
            className="btn btnSuccess"
            id="btn-save"
            data-testid="btn-save-site-information"
            disabled={information.disabled}
            onClick={(e) => {
              e.preventDefault();
              setInformation({ save: new Date().getTime() });
            }}
          >
            {information.loading ? (
              <img id="loading-spinner-2" src={savingspinner} style={{ width: 15 }} />
            ) : (
              <>
                <i className="ti ti-save" /> Save
              </>
            )}
          </button>
          <button
            className="btn btnUrgent"
            id="btn-cancel"
            data-testid="btn-cancel-site-information"
            onClick={(e) => {
              e.preventDefault();
              setInformation({ openForm: false, cancel: new Date().getTime() });
            }}
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          className="btn btnTeal"
          id="btn-edit-site-information"
          data-testid="btn-edit-site-information"
          type="button"
          onClick={() => setInformation({ openForm: true })}
        >
          <i className="ti icon-64 pencil-icon"></i> Edit
        </button>
      )}
    </div>
  );
};

const PatientEngagementActions = () => {
  const { location, setLocation } = useContext(LocationsContext);
  return (
    <div id="location-profile-btn-container" className="axnBtn" style={{ display: "inline-flex" }}>
      {location.openForm ? (
        <>
          <img id="loading-spinner-3" src={savingspinner} style={{ width: 0, height: 0, opacity: 0 }} />
          <button
            className="btn btnSuccess"
            onClick={(e) => {
              e.preventDefault();
              setLocation({ save: new Date().getTime() });
            }}
          >
            {location.loading ? (
              <img id="loading-spinner-4" src={savingspinner} style={{ width: 15 }} />
            ) : (
              <>
                <i className="ti ti-save" /> Save
              </>
            )}
          </button>
          <button
            className="btn btnUrgent"
            onClick={(e) => {
              e.preventDefault();
              setLocation({ openForm: false, cancel: new Date().getTime() });
            }}
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          className="btn btnTeal"
          id="btn-edit-patient-engagement"
          onClick={(e) => {
            e.preventDefault();
            setLocation({ openForm: true });
          }}
        >
          <i className="ti icon-64 pencil-icon"></i> Edit
        </button>
      )}
    </div>
  );
};

const FacilityEsignReportActions = () => {
  const { location, setLocation } = useContext(LocationsContext);
  return (
    <>
      {!location.openForm ? (
        <>
          <button
            className="btn btnTeal"
            id="btn-custom-statement"
            onClick={(e) => {
              e.preventDefault();
              setLocation({ openForm: true });
            }}
          >
            <i className="ti ti-plus" /> Add Custom Statement
          </button>
          <button
            className="btn btnTeal"
            id="btn-add-group"
            onClick={(e) => {
              e.preventDefault();
              setLocation({ openForm: true });
            }}
          >
            <i className="ti ti-plus" /> Add Group
          </button>
        </>
      ) : (
        <>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setLocation({ saved: new Date().getTime() });
            }}
          >
            <button className="btn btnTeal">
              {location.saving ? (
                <img id="loading-spinner-5" src={savingspinner} style={{ width: 15 }} />
              ) : (
                "Save"
              )}
            </button>
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <button
              className="btn"
              onClick={(e) => {
                e.preventDefault();
                setLocation({ openForm: false });
              }}
            >
              Cancel
            </button>
          </a>
        </>
      )}
    </>
  );
};

const InsuranceActions = () => {
  const { insurance, setInsurance } = useContext(LocationsContext);
  return (
    <>
      {insurance.openForm ? (
        <>
          <button className="btn btnTeal" onClick={() => setInsurance({ saved: new Date().getTime() })}>
            {insurance.saving ? (
              <img id="loading-spinner-6" src={savingspinner} style={{ width: 15 }} />
            ) : (
              "Save"
            )}
          </button>
          <button className="btn" onClick={() => setInsurance({ openForm: false })}>
            Cancel
          </button>
        </>
      ) : (
        <button className="btn btnTeal" onClick={() => setInsurance({ openForm: true })}>
          Add
        </button>
      )}
    </>
  );
};

function leadsReducer(state, action) {
  switch (action.type) {
    case "add":
      return { ...state, list: [action.lead, ...state.list], openForm: false, saving: false, saved: null };
    case "update":
      return {
        ...state,
        list: [
          ...state.list.filter((i) => {
            if (i.id == action.lead.id) return action.lead;
            return i;
          }),
        ],
        openForm: false,
        saving: false,
        saved: null,
      };
    case "remove":
      return { ...state, list: [...state.list.filter((i) => i.id != action.lead.id)] };
  }

  return { saved: null, ...state, ...action };
}

function followingPhysicianReducer(state, action) {
  return { ...state, ...action };
}

function hospitalsReducer(state, action) {
  return { ...state, ...action };
}

function rMonitoringReducer(state, action) {
  console.log("#STATE", state);
  console.log("#ACTION", action);
  console.log("#RESULT", { ...state, ...action });
  return { ...state, ...action };
}

function insuranceReducer(state, action) {
  switch (action.type) {
    case "remove-list":
      return {
        list: state.list.filter((i) => i.id != action.data.id),
      };
    case "add-list":
      return {
        saved: null,
        saving: false,
        list: [action.data, ...state.list],
      };
    case "update-list":
      return {
        ...state,
        saving: null,
        saved: null,
        list: [
          ...state.list.map((i) => {
            if (i.id === action.data.id) return action.data;
            return i;
          }),
        ],
      };
  }
  return { ...state, saving: null, saved: null, ...action };
}

function patientReducer(state, action) {
  return { ...state, ...action };
}

const Locations = ({ ...props }) => {
  const { pathname } = props?.location;
  const { path, url } = props?.match;
  const [location, setLocation] = useState(null);
  const { currentLocation } = useContext(LayoutContext);
  const [information, setInformation] = useState({});
  const [patientEngagement, setPatientEngagement] = useState({ loading: false, list: [] });
  const [savePatientNote, setSavePatientNote] = useState();
  const [devices, setDevices] = useState({ loading: false, list: [], saving: false });
  const [leads, setLeads] = useReducer(leadsReducer, { openForm: false, list: [] });
  const [followingPhysicians, setFollowingPhysicians] = useReducer(followingPhysicianReducer, {
    openForm: false,
    readPhysicians: [],
  });
  const [hospital, setHospital] = useReducer(hospitalsReducer, {});
  const [rMonitoring, setRMonitoring] = useReducer(rMonitoringReducer, {});
  const [insurance, setInsurance] = useReducer(insuranceReducer, {});
  const [patientData, setPatientData] = useReducer(patientReducer, {});
  const { i } = useParams();

  useEffect(() => {
    setLocation(currentLocation);
  }, [currentLocation]);

  const fetchPatientNotes = async () => {
    setPatientEngagement({ ...patientEngagement, loading: true });
    try {
      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/patientnote/",
        urlParams: {
          where: '{"patient.id":{"$eq":"' + i + '"}}',
        },
      });
      console.log(data);
      setPatientEngagement({ ...patientEngagement, loading: false, list: data });
    } catch (err) {
      setPatientEngagement({ ...patientEngagement, loading: false });
      console.log(err);
    }
  };

  const fetchDeviceInfo = async () => {
    try {
      updateDevices({ loading: true });
      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/devices/${i}`,
      });
      updateDevices({ list: data, loading: false });
    } catch (err) {
      updateDevices({ loading: false });
      console.log(err);
    }
  };

  const updatePatientEngagement = (data) => setPatientEngagement({ ...patientEngagement, ...data });
  const updateInformation = (data) => setInformation({ ...information, ...data });
  const updateDevices = (data) => setDevices({ ...devices, ...data });

  if (!location) return null;
  return (
    <LocationsContext.Provider
      value={{
        location,
        setLocation: (data) => setLocation(data),
        fetchPatientNotes,
        patientNotes: patientEngagement,
        setPatientEngagement: updatePatientEngagement,
        information,
        setInformation: updateInformation,
        fetchDeviceInfo,
        devices,
        setDevices: updateDevices,
        leads,
        setLeads,
        addPatientNotes: (data) => {
          const list = [...patientEngagement.list, data];
          list.sort((a, b) => a.sticky < b.sticky);
          console.log(list);
          setPatientEngagement({ ...patientEngagement, list, openForm: false });
        },
        setSavePatientNote: (data) => setSavePatientNote(data),
        savePatientNote,
        followingPhysicians,
        setFollowingPhysicians,
        hospital,
        setHospital,
        rMonitoring,
        setRMonitoring,
        insurance,
        setInsurance,
        patientData,
        setPatientData,
      }}
    >
      <div id="patient-profile" className="containerFlex maxHd">
        <div className="contentArea">
          <div className="axnArea mb0">
            <div className="row rowStretch mb0">
              <div className="col col100 alignLeft selectsm">
                <div className="selectsm">
                  <select className="m0">
                    <option>Site Information</option>
                    <option>Remote Monitoring Settings</option>
                    <option>Patient Engagement</option>
                    <option>Facility eSign Report Settings</option>
                    <option>Facility Documentation</option>
                  </select>
                </div>
              </div>
              <div className="axnArea mb0">
                <div className="row rowStretch mb0">
                  <div className="col col-s-100 col40 alignLeft title-back">
                    <h3>
                      Location Profile: <span>{location.name}</span>
                    </h3>
                    <Link to="/locations3" className="urlBack">
                      <i className="ti ti-arrow-left"> </i>Back
                    </Link>
                  </div>
                  <div
                    className="col col-s-100 col60 alignRight btn-group"
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Route exact path={path}>
                      <SiteInformationActions />
                    </Route>
                    <Route path={url + "/patient-engagement"}>
                      <PatientEngagementActions />
                    </Route>
                    <Route path={url + "/facility-esign-report"}>
                      <FacilityEsignReportActions />
                    </Route>
                    <Route path={url + "/facility-doc"}>
                      <InsuranceActions />
                    </Route>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col20 side-nav showBs">
              <nav className="tabs tabY">
                <Link className={pathname === url ? "tabActive" : null} to={url}>
                  <span className="tab-text">Site Information</span>
                </Link>
                <Link
                  className={pathname === url + "/remote-monitoring" ? "tabActive" : null}
                  to={url + "/remote-monitoring"}
                >
                  <span className="tab-text">Remote Monitoring Settings</span>
                </Link>
                <Link
                  className={pathname === url + "/patient-engagement" ? "tabActive" : null}
                  to={url + "/patient-engagement"}
                >
                  <span className="tab-text">Patient Engagement</span>
                </Link>
                <Link
                  className={pathname === url + "/facility-esign-report" ? "tabActive" : null}
                  to={url + "/facility-esign-report"}
                >
                  <span className="tab-text">Facility eSign Report Settings</span>
                </Link>
                <Link
                  className={pathname === url + "/facility-doc" ? "tabActive" : null}
                  to={url + "/facility-doc"}
                >
                  <span className="tab-text">Facility Documentation</span>
                </Link>
              </nav>
            </div>
            <div className="col col-s-100 col80 force-max">
              <Switch>
                <Route exact path={path}>
                  <Information />
                </Route>
                <Route path={path + "/remote-monitoring"}>
                  <RemoteMonitoring />
                </Route>
                <Route path={path + "/patient-engagement"}>
                  <PatientEngagement />
                </Route>
                <Route path={path + "/facility-esign-report"}>
                  <FacilityEsignReport />
                </Route>
                <Route path={path + "/facility-doc"}>
                  <FacilityDoc />
                </Route>
              </Switch>
            </div>
          </div>
        </div>
      </div>
    </LocationsContext.Provider>
  );
};

export default withRouter(Locations);
